/**
 * 🔴 **A 401 MID-SESSION IS NOT A SCREEN'S ERROR TO SHOW** (PNK-83).
 *
 * Devin, 2026-09-10, after the smoke backend was restarted with a different signing key: the Artists
 * board printed *"invalid passport session: bad_signature"* and, under it, *"No artists yet"*. Both
 * were wrong in the same way — the SESSION was gone, and a module was left to say so in whatever
 * words the API happened to use, on top of an empty state that was a statement about data nobody
 * had been allowed to read.
 *
 * So a 401 is announced, not rendered. Every hosted module's API client dispatches
 * `pinksy:session-gone` on the window when a request comes back 401; the shell — which owns the
 * session — signs the person out, remembers where they were, shows sign-in, and puts them back on
 * that page once they are in. The module never decides what a 401 means; it only says one happened.
 *
 * The same shape as `pinksy:navigate`: a CustomEvent, because a module bundle must not import the
 * shell and the shell must not import a module.
 */
export const SESSION_GONE_EVENT = "pinksy:session-gone";

/** Where the person was when the session went — kept for one sign-in, in this tab only. */
export const RETURN_TO_KEY = "pinksy:session:return-to";

export interface SessionGoneDetail {
  /** The API path that answered 401 — for the log, never for the screen. */
  path?: string;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const storage = (): StorageLike | null => {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
};

/**
 * Remember the page to come back to. The root is not worth remembering — sign-in lands there anyway —
 * and anything that is not a same-origin path is refused: a stored value is a redirect target later,
 * and a redirect target that could be a URL is an open redirect.
 */
export function rememberReturnTo(pathname: string, search: string, store: StorageLike | null = storage()): void {
  if (store === null) return;
  const path = `${pathname}${search}`;
  if (!path.startsWith("/") || path.startsWith("//") || path === "/") return;
  try {
    store.setItem(RETURN_TO_KEY, path);
  } catch {
    // Storage full or refused: the person lands on home after sign-in, which is the old behaviour.
  }
}

/** Take the remembered page, once. A second sign-in has nothing to return to. */
export function consumeReturnTo(store: StorageLike | null = storage()): string | null {
  if (store === null) return null;
  try {
    const held = store.getItem(RETURN_TO_KEY);
    store.removeItem(RETURN_TO_KEY);
    return held !== null && held.startsWith("/") && !held.startsWith("//") ? held : null;
  } catch {
    return null;
  }
}

/**
 * Listen for a module announcing a 401. Returns the un-listen.
 *
 * 🔴 A screen that has just lost its session fires MANY 401s at once — a grid, its faces, the drawer.
 * `onGone` is called for the first and the rest are dropped until the next call to `reset`, which the
 * shell makes when somebody signs in again; one lost session is one sign-out, not thirty.
 */
export function listenForSessionGone(onGone: (detail: SessionGoneDetail) => void, target: EventTarget = window): { stop: () => void; reset: () => void } {
  let announced = false;
  const handler = (event: Event): void => {
    if (announced) return;
    announced = true;
    onGone(((event as CustomEvent<SessionGoneDetail>).detail ?? {}) as SessionGoneDetail);
  };
  target.addEventListener(SESSION_GONE_EVENT, handler);
  return {
    stop: () => target.removeEventListener(SESSION_GONE_EVENT, handler),
    reset: () => {
      announced = false;
    },
  };
}
